<template>
    <h1>{{ title }}</h1>

    <div class="row" v-if="!isLoading && !isProcessing">
        <div class="col-md-12" v-if="errorMessage && errorMessage.length > 0">
            <Alert type="danger" :message="errorMessage" />
        </div>
        <div class="col-md-5">
            <form @submit.prevent="handlePasswordChange">
                <div v-if="currentPasswordRequired" class="form-group">
                    <label>Current Password</label>
                    <input :type="showPassword ? 'text' : 'password'"
                           v-model="form.current_password"
                           placeholder="Current Password"
                           class="form-control">

                    <i class="fas toggle-password-eye" :class="showPassword ? 'fa-eye' : 'fa-eye-slash'" @click="changeShowHide()"></i>
                </div>

                <div class="form-group">
                    <label>New Password</label>
                    <input :type="showPassword ? 'text' : 'password'"
                           v-model="form.new_password"
                           placeholder="New Password"
                           class="form-control">

                    <i class="fas toggle-password-eye" :class="showPassword ? 'fa-eye' : 'fa-eye-slash'" @click="changeShowHide()"></i>
                </div>


                <div class="form-group">
                    <label>Confirm New Password</label>
                    <input :type="showPassword ? 'text' : 'password'"
                           v-model="form.confirm_new_password"
                           placeholder="Confirm New Password"
                           class="form-control">

                    <i class="fas toggle-password-eye" :class="showPassword ? 'fa-eye' : 'fa-eye-slash'" @click="changeShowHide()"></i>
                </div>

                <div class="text-right login-buttons">
                    <button type="submit" class="btn btn-primary" :disabled="v$.$invalid">
                        {{ changePasswordLabel }}
                    </button>
                </div>
            </form>
        </div>
        <div class="col-md-7">
            <PasswordRequirementsValidator :password="form.new_password" :confirm-password="form.confirm_new_password" :requirements="passwordRequirements"/>
        </div>
    </div>
    <div v-else>
        <Loader :wait-text="isLoading ? 'Loading...' : (isProcessing ? 'Processing request...' : 'Please wait...')"/>
    </div>
</template>

<script>
import {useLoading} from "@/hooks/use-loading";
import {showHidePassword} from "@/hooks/show-hide-password";
import {onMounted, ref} from "vue";
import {useForm} from "@/hooks/use-form";
import required from "vuelidate/lib/validators/required";
import sameAs from "vuelidate/lib/validators/sameAs";
import {getPasswordRequirements} from "@/api/user";

import PasswordRequirementsValidator from "@/pages/common/PasswordRequirementsValidator";
import {getPasswordErrors} from "@/lib/check-password";
import requiredIf from "vuelidate/lib/validators/requiredIf";
import Loader from "@/components/Loader";
import Alert from "@/components/Alert";

export default {
    name: "PasswordChangeForm",
    components: {Alert, Loader, PasswordRequirementsValidator},
    props: {
        title: String,
        errorMessage: String,
        isProcessing: Boolean,
        currentPasswordRequired: Boolean,
        changePasswordLabel: {
            default: 'Change Password',
            type: String
        }
    },
    emits: [
        'change-password'
    ],
    setup(props, {emit}) {
        const {isLoading, startLoading, stopLoading} = useLoading(false);
        const {showPassword, changeShowHide} = showHidePassword(false);

        const passwordRequirements = ref({
            password_min_lower_case_letters: 0,
            password_min_upper_case_letters: 0,
            minimum_password_length: 12,
            password_min_number_of_alphanumeric: 0,
            password_min_special_characters: 0
        });

        const {form, v$} = useForm({
            props: {
                current_password: '',
                new_password: '',
                confirm_new_password: ''
            },
            rules: {
                current_password: {required: requiredIf(() => props.currentPasswordRequired)},
                new_password: {
                    required,
                    sameAs: sameAs(() => form.confirm_new_password),
                    requirementsMustSatisfy: () => getPasswordErrors(form.new_password, passwordRequirements.value).length === 0
                },
                confirm_new_password: {required, sameAs: sameAs(() => form.new_password)},
            }
        });

        const loadRequirements = async () => {
            startLoading();

            try {
                const {data} = await getPasswordRequirements();
                passwordRequirements.value = data.result;
            } catch (e) {
                console.log(e);
            } finally {
                stopLoading();
            }
        };

        onMounted(() => loadRequirements());

        const handlePasswordChange = async () => {
            if (v$.value.$invalid) {
                return;
            }

            emit('change-password', {...form});
        };

        const reset = () => {
            form.current_password = '';
            form.new_password = '';
            form.confirm_new_password = '';
        };

        return {
            isLoading,
            passwordRequirements,
            handlePasswordChange,
            reset,
            form,
            v$,
            showPassword,
            changeShowHide
        }
    }
}
</script>